import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiArrowLeft, FiSave } from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import FormInput from '../../components/FormInput/FormInput'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import './CreateModule.css'

const CreateModule = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [courses, setCourses] = useState([])
  const [batches, setBatches] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [formData, setFormData] = useState({
    course: '',
    batch: '',
    title: '',
    description: '',
    order: '',
    duration: '',
    isOpen: false,
  })

  useEffect(() => {
    loadCourses()
  }, [])

  useEffect(() => {
    if (formData.course) {
      loadBatchesForCourse(formData.course)
    } else {
      setBatches([])
      setFormData((prev) => ({ ...prev, batch: '' }))
    }
  }, [formData.course])

  const loadCourses = async () => {
    try {
      const response = await adminApi.get('/courses', { params: { limit: 1000 } })
      const coursesList = response.data?.courses || response.data?.data || []
      setCourses(coursesList)
    } catch (err) {
      console.error('Failed to load courses:', err)
    }
  }

  const loadBatchesForCourse = async (courseId) => {
    try {
      const response = await adminApi.get('/batches', {
        params: { courseId, limit: 1000 },
      })
      const batchesList = response.data?.batches || response.data?.data || []
      setBatches(batchesList)

      // Set selected course for display
      const course = courses.find((c) => c._id === courseId)
      setSelectedCourse(course || null)
    } catch (err) {
      console.error('Failed to load batches:', err)
      setBatches([])
    }
  }

  const loadModulesForBatch = async (batchId) => {
    try {
      const response = await adminApi.get(`/batches/${batchId}/modules`)
      const modules = response.data?.modules || response.data?.data || []
      // Set next order number
      if (modules.length > 0) {
        const maxOrder = Math.max(...modules.map((m) => m.order || 0))
        setFormData((prev) => ({ ...prev, order: maxOrder + 1 }))
      } else {
        setFormData((prev) => ({ ...prev, order: 1 }))
      }
    } catch (err) {
      console.error('Failed to load modules:', err)
      setFormData((prev) => ({ ...prev, order: 1 }))
    }
  }

  useEffect(() => {
    if (formData.batch) {
      loadModulesForBatch(formData.batch)
    }
  }, [formData.batch])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (!formData.course || !formData.batch) {
      setError('Please select both course and batch')
      setLoading(false)
      return
    }

    try {
      const response = await adminApi.post(`/batches/${formData.batch}/modules`, {
        title: formData.title,
        description: formData.description,
        order: parseInt(formData.order),
        duration: formData.duration,
        isOpen: formData.isOpen,
      })
      navigate(`/modules/${response.data._id}`)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create module')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="create-module-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Module Management</p>
          <h1>Create New Module</h1>
          <p className="subhead">Create a new module for a batch</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/modules')}>
          <FiArrowLeft size={18} />
          Cancel
        </Button>
      </header>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-section">
          <h2>Course & Batch Selection</h2>
          <Select
            label="Course *"
            name="course"
            value={formData.course}
            onChange={(e) => {
              const courseId = e.target.value
              const course = courses.find((c) => c._id === courseId)
              setSelectedCourse(course || null)
              setFormData({ ...formData, course: courseId, batch: '' })
            }}
            options={[
              { value: '', label: 'Select a course' },
              ...courses.map((c) => ({ value: c._id, label: c.title })),
            ]}
            required
          />
          {selectedCourse && (
            <div className="course-info-card">
              <div className="course-info-header">
                <h3>Selected Course</h3>
                <span className="course-badge">Course</span>
              </div>
              <div className="course-info-content">
                <div className="course-info-item">
                  <strong>Course Name:</strong>
                  <span>{selectedCourse.title}</span>
                </div>
                {selectedCourse.description && (
                  <div className="course-info-item">
                    <strong>Description:</strong>
                    <span>{selectedCourse.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <Select
            label="Batch *"
            name="batch"
            value={formData.batch}
            onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
            options={[
              { value: '', label: formData.course ? 'Select a batch' : 'Select a course first' },
              ...batches.map((b) => ({ value: b._id, label: b.batchName })),
            ]}
            required
            disabled={!formData.course || batches.length === 0}
          />
          {formData.course && batches.length === 0 && (
            <p className="info-text">No batches found for the selected course</p>
          )}
        </div>

        <div className="form-section">
          <h2>Module Information</h2>
          <FormInput
            label="Module Title *"
            name="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Introduction to Web Development"
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Module description and learning objectives..."
          />
          <div className="form-row">
            <FormInput
              label="Order *"
              name="order"
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || '' })}
              required
              min="1"
            />
            <FormInput
              label="Duration"
              name="duration"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              placeholder="e.g., 2 weeks, 1 month"
            />
          </div>
          <div className="form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.isOpen}
                onChange={(e) => setFormData({ ...formData, isOpen: e.target.checked })}
              />
              <span>Module is open for students</span>
            </label>
          </div>
        </div>

        {formData.course && formData.batch && (
          <div className="context-info">
            <div className="context-breadcrumb">
              <span className="context-item">
                <strong>Course:</strong> {selectedCourse?.title || 'Unknown Course'}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item">
                <strong>Batch:</strong>{' '}
                {batches.find((b) => b._id === formData.batch)?.batchName || 'Unknown Batch'}
              </span>
              <span className="context-separator">→</span>
              <span className="context-item active">
                <strong>Module:</strong> {formData.title || 'New Module'}
              </span>
            </div>
            <div className="context-note">
              <p>
                ✓ This module will be created for{' '}
                <strong>
                  {batches.find((b) => b._id === formData.batch)?.batchName || 'the selected batch'}
                </strong>{' '}
                in <strong>{selectedCourse?.title || 'the selected course'}</strong>
              </p>
            </div>
          </div>
        )}

        <div className="form-actions">
          <Button variant="secondary" type="button" onClick={() => navigate('/modules')}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            <FiSave size={18} />
            Create Module
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CreateModule

